import {
  Input,
  Button,
  Typography,
} from "@material-tailwind/react";
import Toaster from "../../components/Toaster";
import { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import * as Yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ShowLoader from "@/components/Loader";
import ScrollToTop from "@/components/ScrollToTop";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { fetchPolicy, savePolicy, resetStatus } from "@/store/slices/policySlice";
import useHasPermission from "@/components/useHasPermission";
import { ClipboardDocumentCheckIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";

const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
};

const getValidationSchema = (t) => Yup.object({
  title: Yup.string()
    .trim()
    .required(t("validation.titleRequired"))
    .min(3, t("validation.titleMin"))
    .max(100, t("validation.titleMax")),
  content: Yup.string()
    .required(t("validation.contentRequired"))
    .test("is-empty", t("validation.contentEmpty"), (value) => {
      const plainText = stripHtml(value);
      return plainText.trim().length > 0;
    }),
});

export function PolicyForm({ type = 'terms', isView = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation("policy");

  const validationSchema = useMemo(() => getValidationSchema(t), [t]);
  
  const displayTitle = type === 'terms' ? t("terms") : t("privacy");

  const selectedPolicy = useSelector((state) => state.policy.selected);
  const { loading } = useSelector((state) => state.policy);

  const [isEditMode, setIsEditMode] = useState(false); 

  const hasPolicyAddPermission = useHasPermission('privacy-policy.add');
  const hasPolicyUpdatePermission = useHasPermission('privacy-policy.update');
  const hasTermAddPermission = useHasPermission('terms-&-conditions.add');
  const hasTermUpdatePermission = useHasPermission('terms-&-conditions.update');

  let showSubmitButton = false;
  if (type === 'privacy') {
    showSubmitButton = isEditMode ? hasPolicyUpdatePermission : hasPolicyAddPermission;
  } else if (type === 'terms') {
    showSubmitButton = isEditMode ? hasTermUpdatePermission : hasTermAddPermission;
  }

  const { register, handleSubmit, control, reset, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      title: '',
      content: '',
      type: type,
    }
  });

  useEffect(() => {
    dispatch(fetchPolicy(type));
  }, [dispatch, type]);

  useEffect(() => {
    if (selectedPolicy) {
      if (selectedPolicy.title || selectedPolicy.content) {
        setIsEditMode(true);
      }
      reset({ 
        title: selectedPolicy.title || '', 
        content: selectedPolicy.content || '', 
        type: type 
      });
    } else {
      reset({ title: '', content: '', type: type });
    }
  }, [selectedPolicy, reset, type]);

  const onSubmit = async (data) => {
    try {
      const result = await dispatch(savePolicy(data)).unwrap();
      if (result.status) {
        Toaster.success(result.message || t("toaster.saveSuccess", { policyName: displayTitle }));
        dispatch(resetStatus());
        navigate(`/dashboard/home`);
      } else {
        Toaster.error(result.message || t("toaster.somethingWentWrong"));
        dispatch(resetStatus());
      }
    } catch (err) {
      Toaster.error(err?.message || t("toaster.saveFailed", { policyName: displayTitle }));
      dispatch(resetStatus());
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  const [copied, setCopied] = useState(false);

  const publicPath = type === 'terms' ? 'terms-conditions' : 'privacy-policy';
  const publicPolicyLink = `${window.location.origin}/${publicPath}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicPolicyLink);
    setCopied(true);
    Toaster.success(t("toaster.linkCopied", { policyName: displayTitle }));
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-5 mb-8 flex flex-col gap-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {loading && <ShowLoader />}
      <ScrollToTop />
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <div className="sticky top-0 z-30 mt-1 mb-6 p-5 bg-blue-gray-f5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2 ">
            <div className="flex items-center gap-2 cursor-pointer">
              <Typography variant="h5" color="blue-gray" className="font-extrabold text-gray-800">
                {isView ? `${t('view')} ${displayTitle}` : isEditMode ? `${t('edit')} ${displayTitle}` : `${t('add')} ${displayTitle}`}
              </Typography>
            </div>
            
            {!isView &&
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(`/dashboard`)}
                  className="rounded-lg"
                >
                  {t("cancel")}
                </Button>
                {showSubmitButton && (
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white"
                  >
                    {loading ? t("saving") : isEditMode ? t("update") : t("save")}
                  </Button>
                )}
              </div>
            }
          </div>
          
          {/* <div className="flex items-center gap-1">
            <Link to={`/dashboard`} className="flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              {t("back")}
            </Link>
          </div> */}
        </div>

        <fieldset disabled={isView}>
          <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white ">
            <div className="mb-5 pb-2 border-b border-blue-gray-200">
              <Typography variant="h4" color="blue-gray">
                {t("contentDetails")}
              </Typography>
            </div>
            
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-1">
              <div>
                <Typography variant="small" color="blue-gray" className="mb-3 font-medium">
                  {t("pageTitle")} <span className="text-red-500">*</span>
                </Typography>
                <Input
                  type="text"
                  size="lg"
                  className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{ className: "before:content-none after:content-none" }}
                  placeholder={t("titlePlaceholder")}
                  {...register('title')}
                  error={Boolean(errors.title)}
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
                )}
              </div>

              <div>
                <Typography variant="small" color="blue-gray" className="mb-3 font-medium">
                  {t("pageContent")} <span className="text-red-500">*</span>
                </Typography>
                
                <div className={`rounded-md ${errors.content ? 'border border-red-500' : ''}`}>
                  <Controller
                    name="content"
                    control={control}
                    render={({ field }) => (
                      <ReactQuill
                        {...field}
                        theme="snow"
                        modules={quillModules}
                        placeholder={t("contentPlaceholder")}
                        readOnly={isView}
                        className="bg-white h-72 mb-12"
                      />
                    )}
                  />
                </div>
                {errors.content && (
                  <p className="text-red-500 text-sm mt-1">{errors.content.message}</p>
                )}
              </div>
            </div>
          </div>
        </fieldset>
      </form>

      <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
        <Typography variant="h6" color="blue-gray" className="mb-5">
          {t("publicLink", { policyName: displayTitle })}
        </Typography>
        <div className="flex flex-col sm:flex-row items-stretch gap-4 w-full">
          <div className="flex-1">
            <Input
              readOnly
              value={publicPolicyLink}
              className="w-full !border-t-blue-gray-200 focus:!border-t-gray-900"
              labelProps={{
                className: "before:content-none after:content-none",
              }}
            />
          </div>
          <div className="w-full sm:w-[200px] flex-shrink-0">
            <Button
              type="button"
              disabled={loading}
              onClick={handleCopyLink}
              className={`w-full flex items-center justify-center gap-2 px-5 py-2.5 capitalize font-semibold text-white rounded-lg shadow-sm transition-all duration-300
          ${copied ? "bg-green-600 hover:bg-green-700" : "bg-black hover:bg-gray-800"}`}
            >
              <ClipboardDocumentCheckIcon className="h-5 w-5" />
              {copied ? t("copied") : t("copyLink")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PolicyForm;
